/**
 * Global search for a report's loaded rows.
 *
 * Filters client-side across every VISIBLE column, so what the box searches is
 * always what the user can see — hiding a column also removes it from the
 * search, which is the behaviour people expect from a table search.
 *
 * Client-side is right here: both reports fetch their whole bounded result set
 * up front (the filter drawer bounds it), so a round trip per keystroke would
 * be slower and no more accurate.
 */
import { useMemo, type JSX } from 'react'
import { Search, X } from 'lucide-react'

interface ReportSearchProps {
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  /** Placeholder — name the thing being searched, not just "Search". */
  placeholder?: string
}

export default function ReportSearch({
  value,
  onChange,
  disabled = false,
  placeholder = 'Search results…',
}: ReportSearchProps): JSX.Element {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      <input
        type="search"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-8 w-56 rounded-[3px] border border-slate-200 bg-white pl-8 pr-7 text-[12.5px] font-medium text-slate-700 outline-none transition-colors placeholder:font-normal placeholder:text-slate-400 hover:border-slate-300 focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]/10 disabled:cursor-not-allowed disabled:opacity-50"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-[3px] text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}

/**
 * Filter rows by a query across the given columns.
 *
 * Matching is case-insensitive substring on each cell's rendered-ish text. A
 * multi-word query requires EVERY word to appear somewhere in the row (not
 * necessarily the same cell), which is what makes "nikhil closed" behave the
 * way people expect rather than finding nothing.
 */
export function useReportSearch<T>(
  rows: T[],
  query: string,
  columnKeys: string[],
  read: (row: T, key: string) => unknown,
): T[] {
  return useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    const words = q.split(/\s+/).filter(Boolean)

    return rows.filter((row) => {
      const haystack = columnKeys
        .map((k) => {
          const v = read(row, k)
          if (v === null || v === undefined) return ''
          if (Array.isArray(v)) return v.join(' ')
          return String(v)
        })
        .join(' ')
        .toLowerCase()
      return words.every((w) => haystack.includes(w))
    })
  }, [rows, query, columnKeys, read])
}
